import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'

import { fetchNftList, NftListResponse } from '@/api'
import { buildBasePagination } from '@/helpers/pagination'
import { queryKeys } from '@/queries/keys'
import { PaginationParams } from '@/types'

interface QueryOptions {
  enabled?: boolean
  staleTime?: number
  refetchInterval?: number
}

interface UseNftInfiniteListOptions {
  pagination?: PaginationParams
  queryOptions?: QueryOptions
}

export function useNftInfiniteList(options?: UseNftInfiniteListOptions) {
  const basePagination = buildBasePagination(options?.pagination)
  type NftInfiniteData = InfiniteData<NftListResponse, string | undefined>

  return useInfiniteQuery<
    NftListResponse,
    unknown,
    NftInfiniteData,
    ReturnType<typeof queryKeys.nft.list>,
    string | undefined
  >({
    queryKey: queryKeys.nft.list(basePagination),
    initialPageParam: options?.pagination?.key,
    queryFn: ({ pageParam }) =>
      fetchNftList({
        ...basePagination,
        key: pageParam,
      }),
    getNextPageParam: lastPage => lastPage.pagination.next_key ?? undefined,
    enabled: options?.queryOptions?.enabled ?? true,
    staleTime: options?.queryOptions?.staleTime,
    refetchInterval: options?.queryOptions?.refetchInterval,
  }) as UseInfiniteQueryResult<NftInfiniteData, unknown>
}
